import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  cacheProviderSelection,
  detectOnboardingSurface,
  fetchOnboardingBundle,
  persistOnboardingV2,
  persistTraderProfile,
  withAiProviderComplete,
  withDeskComplete,
  withPermissionsComplete,
  withProfileComplete,
  type AiProviderId,
  type OnboardingPermissionId,
  type OnboardingPermissionStatus,
  type OnboardingV2State,
  type TraderProfile,
} from "../../lib/onboarding-v2";
import { isElectron } from "../../lib/platform";
import { requestOnboardingPermission } from "../../lib/onboarding-permissions";
import {
  createOnboardingDesk,
  requestPricedInCapitalInvite,
} from "../../lib/onboarding-desk";
import {
  chooseProfileAvatar,
  runBusySlice,
} from "./welcome-controller-actions";
import {
  emptyProfile,
  nextIncompleteStep,
  profileFromBundle,
} from "./welcome-onboarding-utils";
import type {
  BusyState,
  WelcomeOnboardingProps,
  WelcomeStep,
} from "./welcome-onboarding-types";
import {
  confirmManagedGrokProvider,
  connectGrokProvider,
  saveProviderApiKey,
} from "./welcome-provider-actions";

export function useWelcomeOnboardingController({
  apiBase,
  forceRestart,
  getAccessToken,
  onEnter,
  surface: surfaceOverride,
  userEmail,
}: WelcomeOnboardingProps & { apiBase: string }) {
  const surface = detectOnboardingSurface(surfaceOverride);
  const isDesktopSurface = surface === "desktop" || isElectron();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<WelcomeStep>("profile");
  const [busy, setBusy] = useState<BusyState>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [oauthCommand, setOauthCommand] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingV2State | null>(null);
  const [profile, setProfile] = useState<TraderProfile>(emptyProfile);
  const [permissionStatuses, setPermissionStatuses] = useState<
    Partial<Record<OnboardingPermissionId, OnboardingPermissionStatus>>
  >({});
  const [provider, setProvider] = useState<AiProviderId>("hermes-grok");
  const [apiKey, setApiKey] = useState("");
  useEffect(() => {
    let cancelled = false;
    setBusy("loading");
    fetchOnboardingBundle(getAccessToken, apiBase)
      .then((bundle) => {
        if (cancelled) return;
        const nextProfile = profileFromBundle(bundle);
        const surfaceState = bundle.onboarding.surfaces[surface];
        setOnboarding(bundle.onboarding);
        setProfile(nextProfile);
        setPermissionStatuses(surfaceState?.permissions?.items ?? {});
        setProvider(surfaceState?.aiProvider?.provider ?? "hermes-grok");
        const nextStep = nextIncompleteStep(bundle, surface, nextProfile);
        setStep(forceRestart ? "profile" : nextStep);
        setBusy("idle");
      })
      .catch(() => {
        if (!cancelled) {
          setOnboarding({ version: 2, surfaces: {} });
          setBusy("idle");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, forceRestart, getAccessToken, surface]);
  function currentOnboarding(): OnboardingV2State {
    return onboarding ?? { version: 2, profile, surfaces: {} };
  }
  async function saveSlice(state: BusyState, action: () => Promise<void>) {
    await runBusySlice({ action, setBusy, setMessage, state });
  }
  function updateProfileField<K extends keyof TraderProfile>(
    key: K,
    value: TraderProfile[K],
  ) {
    setProfile((prev) => ({ ...prev, [key]: value }));
    setMessage(null);
  }
  async function chooseAvatar(file: File | null) {
    await chooseProfileAvatar({
      file,
      onAvatarUrl: (url) => updateProfileField("avatarUrl", url),
      setBusy,
      setMessage,
    });
  }
  async function saveProfile(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized: TraderProfile = {
      ...profile,
      brokerOrPropFirm: profile.brokerOrPropFirm.trim().slice(0, 72),
      nameTag: profile.nameTag.trim().slice(0, 40),
      yearsExperience: 0,
    };
    if (!normalized.nameTag) return setMessage("Name tag is required.");
    await saveSlice("saving", async () => {
      await persistTraderProfile(getAccessToken, normalized, apiBase);
      const next = withProfileComplete(
        currentOnboarding(),
        surface,
        normalized,
      );
      await persistOnboardingV2(getAccessToken, next, apiBase);
      setProfile(normalized);
      setOnboarding(next);
      setStep("permissions");
      setMessage("Profile saved.");
    });
  }
  async function requestPermission(id: OnboardingPermissionId) {
    setBusy("saving");
    const status = await requestOnboardingPermission({
      apiBase,
      getAccessToken,
      permission: id,
    }).catch(() => "unknown" as OnboardingPermissionStatus);
    setPermissionStatuses((prev) => ({ ...prev, [id]: status }));
    setBusy("idle");
  }
  function skipPermission(id: OnboardingPermissionId) {
    setPermissionStatuses((prev) => ({ ...prev, [id]: "skipped" }));
    setMessage(null);
  }
  async function savePermissions() {
    await saveSlice("saving", async () => {
      const next = withPermissionsComplete(
        currentOnboarding(),
        surface,
        permissionStatuses,
      );
      await persistOnboardingV2(getAccessToken, next, apiBase);
      setOnboarding(next);
      setStep("desk");
      setMessage("Permission choices saved.");
    });
  }
  async function completeDesk(
    deskId: string,
    deskName: string,
    status: "created" | "requested" | "skipped",
  ) {
    const nextProfile = { ...profile, deskId, deskName };
    const next = withDeskComplete(
      { ...currentOnboarding(), profile: nextProfile },
      surface,
      { deskId, deskName, status },
    );
    await persistOnboardingV2(getAccessToken, next, apiBase);
    setProfile(nextProfile);
    setOnboarding(next);
    setStep("ai");
  }

  async function joinPricedInCapital() {
    await saveSlice("saving", async () => {
      const result = await requestPricedInCapitalInvite({
        apiBase,
        getAccessToken,
        profile,
        userEmail,
      });
      await completeDesk(
        result.deskId,
        result.deskName,
        result.softBypass ? "skipped" : "requested",
      );
      setMessage(
        result.softBypass
          ? "Default desk unavailable. Continuing without a desk."
          : `PIC invite requested. Push fanout: ${result.notificationPushed ?? 0}.`,
      );
    });
  }
  async function createDesk(name: string) {
    await saveSlice("saving", async () => {
      const result = await createOnboardingDesk({
        apiBase,
        getAccessToken,
        name,
      });
      await completeDesk(result.deskId, result.deskName, "created");
      setMessage("Desk created.");
    });
  }
  async function finishProvider(
    selected: AiProviderId,
    status: "connected" | "key-set",
  ) {
    const next = withAiProviderComplete(
      currentOnboarding(),
      surface,
      selected,
      status,
    );
    await persistOnboardingV2(getAccessToken, next, apiBase);
    cacheProviderSelection(selected);
    setOnboarding(next);
    onEnter();
  }
  async function connectGrok() {
    await saveSlice("connecting", async () => {
      await connectGrokProvider({
        finishProvider,
        isDesktopSurface,
        setMessage,
        setOauthCommand,
        surface,
      });
    });
  }
  async function confirmManagedGrok() {
    await saveSlice("connecting", async () =>
      confirmManagedGrokProvider(finishProvider),
    );
  }
  async function saveApiKey(selected: AiProviderId) {
    await saveSlice("saving", async () => {
      await saveProviderApiKey({
        apiBase,
        apiKey,
        finishProvider,
        getAccessToken,
        provider: selected,
      });
      setApiKey("");
    });
  }
  async function submitProvider() {
    if (provider === "hermes-grok") return connectGrok();
    if (provider === "hermes-set-model")
      return finishProvider("hermes-set-model", "connected");
    return saveApiKey(provider);
  }

  return {
    actions: {
      chooseAvatar,
      confirmManagedGrok,
      createDesk,
      joinPricedInCapital,
      requestPermission,
      savePermissions,
      saveProfile,
      skipPermission,
      submitProvider,
      updateProfileField,
    },
    apiKey,
    busy,
    fileRef,
    isBusy: busy !== "idle",
    message,
    oauthCommand,
    permissionStatuses,
    profile,
    provider,
    setApiKey: (value: string) => {
      setApiKey(value);
      setMessage(null);
    },
    setProvider: (value: AiProviderId) => {
      setProvider(value);
      setMessage(null);
    },
    step,
    surface,
  };
}
